import { useMemo, useState } from "react";
import { api } from "@/lib/kfmApi";
import CopyButton from "@/components/CopyButton";
import { ctaTextLine } from "@/lib/studyText";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { HelpCircle, Loader2, Languages, Columns2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/kfmApi";
import PronounceButton from "@/components/PronounceButton";
import BookmarkButton from "@/components/BookmarkButton";
import CompareVersions from "@/components/CompareVersions";
import HighlightableText from "@/components/HighlightableText";

// Shared Scripture hero block: verses + copyright + copy + the "I don't understand
// this" explainer. Words that have a Strong's entry get a subtle dotted underline
// and open their Hebrew/Greek entry on tap (inline concordance).
export default function ScriptureHero({ session, highlights = [], onHighlightsChange, sharePath }) {
  const passage = session.passage || {};
  const verses = passage.verses || [];
  const strongs = (session.study && session.study.strongs) || [];
  const [explanation, setExplanation] = useState(session.simple_explanation || "");
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [compare, setCompare] = useState(false);

  const scriptureText = verses.map((v) => `${v.verse} ${v.text}`).join(" ");

  // Map single lowercase word tokens -> the Strong's entry that references them.
  const lookup = useMemo(() => {
    const map = {};
    ((session.study && session.study.strongs) || []).forEach((e) => {
      const w = (e.word || "").toLowerCase();
      w.split(/[\s/,()-]+/).forEach((tok) => {
        const k = tok.replace(/[^a-z']/g, "");
        if (k && k.length > 2 && !map[k]) map[k] = e;
      });
    });
    return map;
  }, [session.study]);

  const openEntry = (e) => {
    setActive(e);
    if (session.id && e.strongs_number) {
      api
        .post(`/study/sessions/${session.id}/word-seen`, {
          strongs_number: e.strongs_number,
          word: e.word || "",
          reference: session.study?.passage_reference || "",
        })
        .catch(() => {});
    }
  };

  const explain = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/study/sessions/${session.id}/explain`);
      setExplanation(data.explanation);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load a simple explanation");
    }
    setLoading(false);
  };

  if (verses.length === 0) return null;

  const used = new Set(); // mark only the first occurrence of each word

  const renderVerse = (text) => {
    if (Object.keys(lookup).length === 0) return text;
    const parts = text.split(/(\b[A-Za-z']+\b)/);
    return parts.map((part, i) => {
      const key = part.toLowerCase().replace(/[^a-z']/g, "");
      const entry = lookup[key];
      if (entry && !used.has(entry.strongs_number)) {
        used.add(entry.strongs_number);
        return (
          <button
            key={i}
            data-testid="strongs-inline-word"
            onClick={() => openEntry(entry)}
            className="underline decoration-dotted decoration-[#00E5FF]/50 underline-offset-4 hover:decoration-[#00E5FF] hover:text-[#00E5FF] transition-colors"
          >
            {part}
          </button>
        );
      }
      return part;
    });
  };

  return (
    <section data-testid="scripture-block" className="relative">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Scripture</div>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="compare-toggle"
            onClick={() => setCompare((c) => !c)}
            title="Compare translations side by side"
            aria-pressed={compare}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              compare ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]" : "border-[#29344D] text-slate-400 hover:border-[#00E5FF]/40 hover:text-[#00E5FF]"
            }`}
          >
            <Columns2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Compare
          </button>
          <BookmarkButton
            variant="pill"
            testid="save-verse"
            payload={{
              kind: "verse",
              reference: session.study?.passage_reference || passage.reference || "",
              text: scriptureText,
              translation: passage.translation || "",
            }}
          />
          <CopyButton text={`${scriptureText}\n\n\u2014\n${ctaTextLine("Read & study any passage free with OpenWord AI:", "copy", "verse_export")}`} label="Scripture copied" testid="copy-scripture" />
        </div>
      </div>
      {compare && <CompareVersions session={session} />}
      <div className="text-xl md:text-2xl leading-[1.9] text-white space-y-1">
        {verses.map((v, i) => (
          <span key={i} className="inline">
            <sup data-testid="verse-number" className="text-[#00E5FF] text-xs font-sans font-medium mr-1 align-super">{v.verse}</sup>
            <HighlightableText
              sessionId={session.id}
              block={`scripture-v${v.verse}`}
              text={v.text}
              highlights={highlights}
              onChange={onHighlightsChange}
              renderText={renderVerse}
              sharePath={sharePath}
              cardKind="scripture"
              reference={session.study?.passage_reference || ""}
              translation={passage.translation || ""}
            />{" "}
          </span>
        ))}
      </div>
      {strongs.length > 0 && (
        <p className="text-[11px] text-slate-500 mt-3 flex items-center gap-1.5">
          <Languages className="w-3.5 h-3.5" strokeWidth={1.5} />
          Tap an underlined word for its original Hebrew or Greek.
        </p>
      )}
      {passage.copyright && passage.copyright !== "Public Domain" && (
        <p data-testid="scripture-copyright" className="text-[11px] text-slate-500 mt-4 leading-relaxed">
          {passage.copyright}
        </p>
      )}

      <button
        data-testid="explain-simply"
        onClick={explain}
        disabled={loading}
        className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#10222A] px-5 py-3 text-sm font-medium transition-colors disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <HelpCircle className="w-4 h-4" strokeWidth={1.5} />}
        {"I don't understand this"}
      </button>

      {explanation && (
        <div
          data-testid="explain-result"
          className="mt-4 rounded-2xl bg-[#141B29]/70 border border-[#1E273D] p-5 space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-500"
        >
          <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF]">In plain words</div>
          {explanation
            .split(/\n{2,}/)
            .filter((p) => p.trim())
            .map((p, i) => (
              <p key={i} className="text-base text-slate-300 leading-relaxed">
                {p.trim()}
              </p>
            ))}
        </div>
      )}

      {/* Inline Strong's entry */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#0E131F] max-w-md" data-testid="strongs-inline-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="text-2xl text-white">{active.original}</span>
                  <PronounceButton entry={active} testid="strongs-inline-pronounce" />
                  <BookmarkButton
                    testid="save-word-inline"
                    payload={{
                      kind: "word",
                      strongs_number: active.strongs_number || "",
                      word: active.word || "",
                      original: active.original || "",
                      transliteration: active.transliteration || "",
                      definition: active.definition || "",
                    }}
                  />
                  <span className="text-sm text-slate-400">{active.word}</span>
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Original Hebrew or Greek word details for {active.word}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm text-slate-400">
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span>{active.transliteration}</span>
                  <span className="font-medium text-[#00E5FF]">{active.strongs_number}</span>
                </div>
                <p className="leading-relaxed">
                  <span className="font-medium text-slate-300">Definition. </span>
                  {active.definition}
                </p>
                {active.usage && (
                  <p className="leading-relaxed">
                    <span className="font-medium text-slate-300">In this passage. </span>
                    {active.usage}
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
